import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { FormattedMessage } from "react-intl"
import Tooltip from "components/Tooltip"
import InfoIcon from "assets/icons/info-icon"
import Row from "components/Row"
import Container from "components/Container"
import Col from "components/Col"
import SearchDropdown from "components/SearchDropdown"
import Modal from "components/Modal"
import { requestIUCNListSearch } from "api/iucnList"
import {
  requestCreateSiteSpecie,
  requestDeleteSiteSpecie,
  requestGetAllSiteSpecies,
  requestGetSiteSpecies,
} from "api/siteSpecie"
import {
  requestUpdateSpeciesFiles,
  requestDeleteSpeciesFiles,
  requestGetSpeciesFiles,
  requestSubmitSensitiveSpeciesAcknowledgment,
} from "api/site"
import { delay } from "utils/delay"
import { SiteContext } from "providers/site"
import ItemsPerPageDropdown from "components/ItemsPerPageDropdown"
import Pagination from "features/Pagination"
import LoadingWrapper from "components/LoadingWrapper"
import { useParams } from "react-router-dom"
import { showErrorToast, showSuccessToast } from "utils/toast"
import { useMultiplePagination } from "hooks/useMultiplePagination"
import SensitiveSpeciesWarningModal from "components/Modal/SensitiveSpeciesWarningModal"
import {
  AssessmentPageSubTittle,
  AssessmentPageSubTittleIconWrapper,
  AssessmentPageSubTittleWrapper,
  AssessmentPageTittle,
  AssessmentPageTittleWrapper,
} from "../../style"
import { SpeciesDetailsSearchFormWrapper, SpeciesPaginationWrapper, SpeciesSearchContainer } from "./style"
import RemoveSpeciesModal from "./removeSpeciesModal"
import SpeciesDetailsModal from "./speciesDetailsModal/speciesDetailsModal"
import { defaultSiteProposalValidationResults } from "constants/defaultSiteProposalValidationResults"
import {
  SITE_STATUS_DRAFT_ID,
  SITE_STATUS_EDIT_ONLY_ID,
  SITE_STATUS_NOMINATED_ID,
  SITE_STATUS_SUBMITTED_EDIT_ONLY_ID,
  SITE_STATUS_CONFIRMED_ID,
  SITE_STATUS_DELISTED_ID,
  SITE_STATUS_SUPERSEDED_ID 
} from "constants/siteStatuses"
import SpeciesConfirmDeclineList from "./speciesConfirmDeclineList/speciesConfirmDeclineList"
import { SpeciesListTitle, SpeciesListTitleWrapper } from "./speciesConfirmDeclineList/style"
import { INDIVIDUAL_ROLE_ID, NCG_ROLE_ID, SECRETARIAT_ROLE_ID, RFP_ROLE_ID } from "constants/roles"
import { ProfileContext } from "providers/profile"
import Uploader from "flow/siteProposal/pages/about/uploader"
import UploadSiteFiles from "components/SitesUploads"
import { normFile, uniqByKey, extractFiles } from "utils/fileUploadHelpers"
import { canAccessBlobExplorer } from "utils/rolePermissions"

const defaultPaginationState = {
  totalNumber: 0,
  resultsPerPage: 12,
  totalPages: 1,
  currentPage: 1,
  hasNext: true,
  hasPrevious: false,
}

const tooltipXOffset = 0
const tooltipYOffset = 16
const inputSaveDelayTime = 300
const updateSearchOptionsTimeOut = delay(inputSaveDelayTime)

function isIbaSpeciesDisqualified(item) {
  return (
    item?.disqualifiedIbaTrigger === true ||
    item?.disqualifiedIbaTrigger === 1 ||
    item?.disqualifiedIbaTrigger === '1' ||
    item?.disqualifiedIbaTrigger === 'true' ||
    item?.disqualified_iba_trigger === true ||
    item?.disqualified_iba_trigger === 1 ||
    item?.disqualified_iba_trigger === '1' ||
    item?.disqualified_iba_trigger === 'true' ||
    item?.ibaDisqualified === true ||
    item?.ibaDisqualified === 1 ||
    item?.ibaDisqualified === '1' ||
    item?.ibaDisqualified === 'true'
  )
}

function isSpeciesQualifiedForReassessment(item) {
  if (!item.lastReassessmentDate) {
    return true
  }

  const hasQualifyingTriggerReason = Array.isArray(item.triggerReasonId)
    ? item.triggerReasonId.includes(1)
    : true

  return hasQualifyingTriggerReason
}

function resolveHistoryId(editOnly, site, params) {
  const fromParams =
    params?.siteHistoryId ??
    params?.sitehistoryId ??
    params?.historyId ??
    params?.HistoryId ??
    params?.history_id

  const fromSiteFlat =
    site?.sitehistoryId ??
    site?.siteHistoryId ??
    site?.SiteHistoryId ??
    site?.historyId ??
    site?.HistoryId ??
    site?.history_id ??
    site?.currentSiteHistoryId ??
    site?.currentHistoryId

  const fromSiteNested =
    site?.SiteHistory?.id ??
    site?.siteHistory?.id ??
    site?.history?.id ??
    site?.History?.id

  return editOnly ? (fromSiteFlat ?? fromParams ?? fromSiteNested) : (fromParams ?? fromSiteFlat ?? fromSiteNested)
}

function resolveSiteId(editOnly, site, params) {
  const fromParams = params?.siteId ?? params?.id ?? params?.SiteId
  const fromSite = site?.siteId ?? site?.id ?? site?.SiteId
  return editOnly ? (fromSite ?? fromParams) : (fromParams ?? fromSite)
}

const SpeciesDetailsSearchForm = ({ site }) => {
  const [species, setSpecies] = useState(null)
  const [speciesList, setSpeciesList] = useState([])
  const [newSpecies, setNewSpecies] = useState([])
  const [oldSpecies, setOldSpecies] = useState([])
  const [rejectedSpecies, setRejectedSpecies] = useState([])
  const [searchOptions, setSearchOptions] = useState([])
  const [scientificName, setScientificName] = useState("")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSpeciesDetailsModalOpen, setIsSpeciesDetailsModalOpen] = useState(false)
  const [speciesToWorkWith, setSpeciesToWorkWith] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [taxonId, setTaxonId] = useState(null)
  const [isOpen, setIsOpen] = useState(false)

  const params = useParams()
  const {
    siteProposalFieldValidation,
    setSiteProposalFieldValidation,
    isEditable,
    getMySite,
    getSiteValidation
  } = useContext(SiteContext)
  const normalizedStatusId = Number(site.SiteStatusId)
  const disabled =
    normalizedStatusId !== SITE_STATUS_DRAFT_ID || (!isEditable && normalizedStatusId === SITE_STATUS_DRAFT_ID)
  const editOnlySiteStatus =
    normalizedStatusId === SITE_STATUS_EDIT_ONLY_ID || normalizedStatusId === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID

  const historyId = useMemo(() => resolveHistoryId(editOnlySiteStatus, site, params), [editOnlySiteStatus, site, params])
  const siteId = useMemo(() => resolveSiteId(editOnlySiteStatus, site, params), [editOnlySiteStatus, site, params])
  const isReassessment = Boolean(historyId)

  const { allSpeciesPagination, oldSpeciesPagination, newSpeciesPagination } =
    useMultiplePagination(defaultPaginationState)

  const { profile } = useContext(ProfileContext)
  const roleId = profile?.Role?.id ?? profile?.RoleId
  const isNcgOrIndividualUser = roleId === NCG_ROLE_ID || roleId === INDIVIDUAL_ROLE_ID
  const isSecretariatOrRfp = roleId === SECRETARIAT_ROLE_ID || roleId === RFP_ROLE_ID

  const [uploadedFiles, setUploadedFiles] = useState([])
  const [isFilesLoading, setIsFilesLoading] = useState(false)
  const [isSensitiveWarningOpen, setIsSensitiveWarningOpen] = useState(false)
  const [isSubmittingSensitiveWarning, setIsSubmittingSensitiveWarning] = useState(false)

  const isConfirmed = site.SiteStatusId === SITE_STATUS_CONFIRMED_ID || 
                              site.SiteStatusId === SITE_STATUS_SUPERSEDED_ID || 
                              site.SiteStatusId === SITE_STATUS_DELISTED_ID
  const hasSpecies = useMemo(() => {
    const allSpeciesTotal = Number(allSpeciesPagination?.pagination?.totalNumber || 0)
    const newSpeciesTotal = Number(newSpeciesPagination?.pagination?.totalNumber || 0)
    const oldSpeciesTotal = Number(oldSpeciesPagination?.pagination?.totalNumber || 0)
    const loadedSpeciesCount = speciesList.length + newSpecies.length + oldSpecies.length + rejectedSpecies.length

    // Prefer API totals because current-page arrays can be empty while records exist on other pages.
    return allSpeciesTotal + newSpeciesTotal + oldSpeciesTotal > 0 || loadedSpeciesCount > 0
  }, [
    allSpeciesPagination?.pagination?.totalNumber,
    newSpeciesPagination?.pagination?.totalNumber,
    oldSpeciesPagination?.pagination?.totalNumber,
    speciesList.length,
    newSpecies.length,
    oldSpecies.length,
    rejectedSpecies.length
  ])
  const isKbaSecOrRfp = canAccessBlobExplorer(roleId)
  const showUploader = (!isConfirmed || isKbaSecOrRfp) && hasSpecies
  const hideDownload = isConfirmed

  // Species files are managed separately from site context

  function getSpecies(element) {
    const maybePromise = requestIUCNListSearch?.(element)
    if (!maybePromise || typeof maybePromise.then !== "function") return
    maybePromise.then((data) => {
      if (data.length > 0) {
        const result = data.map((item, index) => {
          return { label: item.scientific_name, value: index, taxonid: item.taxonid }
        })
        setSearchOptions(result)
      } else {
        setSearchOptions([])
      }
    })
  }

  const getSiteSpecies = useCallback(() => {
    setIsLoading(true)
    requestGetAllSiteSpecies(siteId, historyId, {
      resultsPerPage: allSpeciesPagination.pagination.resultsPerPage,
      currentPage: allSpeciesPagination.pagination.currentPage,
    }).then((data) => {
      setIsLoading(false)
      setSpeciesList([...(data?.species || [])])
      allSpeciesPagination.setPagination(data?.pagination || {})
    })
    requestGetAllSiteSpecies(siteId, historyId, {
      resultsPerPage: allSpeciesPagination.pagination.totalNumber,
      currentPage: 1,
    }).then((data) => {
      setIsLoading(false)
      setRejectedSpecies([...(data?.species || [])])
    })
  }, [siteId, historyId, allSpeciesPagination.pagination.resultsPerPage, allSpeciesPagination.pagination.currentPage, allSpeciesPagination.pagination.totalNumber, allSpeciesPagination.setPagination])

  const getNewSiteSpecies = useCallback(() => {
    setIsLoading(true)
    requestGetSiteSpecies(
      siteId,
      historyId,
      {
        resultsPerPage: newSpeciesPagination.pagination.resultsPerPage,
        currentPage: newSpeciesPagination.pagination.currentPage,
      },
      true,
    ).then((data) => {
      setIsLoading(false)
      setNewSpecies([...(data?.species || [])])
      newSpeciesPagination.setPagination(data?.pagination || {})
    })
  }, [siteId, historyId, newSpeciesPagination.pagination.resultsPerPage, newSpeciesPagination.pagination.currentPage, newSpeciesPagination.setPagination])

  const getOldSiteSpecies = useCallback(() => {
    setIsLoading(true)
    requestGetSiteSpecies(
      siteId,
      historyId,
      {
        resultsPerPage: oldSpeciesPagination.pagination.resultsPerPage,
        currentPage: oldSpeciesPagination.pagination.currentPage,
      },
      false,
    ).then((data) => {
      setIsLoading(false)
      setOldSpecies([...(data?.species || [])])
      oldSpeciesPagination.setPagination(data?.pagination || {})
    })
  }, [siteId, historyId, oldSpeciesPagination.pagination.resultsPerPage, oldSpeciesPagination.pagination.currentPage, oldSpeciesPagination.setPagination])

  function addSpeciesToSite(element) {
    const taxonId = element?.taxonid
    if (!taxonId || typeof taxonId === "object") {
      showErrorToast(<FormattedMessage id="invalidTaxonid" />)
      return
    }

    requestCreateSiteSpecie(siteId, taxonId.toString(), params.siteHistoryId).then(
      (data) => {
        // If backend indicates this is a hidden sensitive species for the current user,
        // show the sensitive-warning modal so they can submit a visibility request.
        console.log('Response from createSiteSpecie:', data) // Debug log to check the response structure and values
        if (
          data?.requiresSensitiveVisibilityRequest === true ||
          data?.requiresSensitiveSpeciesAcknowledgment === true ||
          data?.error === "sensitiveSpeciesHiddenForUser"
        ) {
          setTaxonId(taxonId)
          setIsSensitiveWarningOpen(true)
          return
        }

        if (data?.error) {
          if (typeof data.error === "string") {
            return showErrorToast(<FormattedMessage id={data.error} />)
          }
          return
        }
        setSpeciesToWorkWith(data)
        setIsSpeciesDetailsModalOpen(true)
        getSiteSpecies()
        getNewSiteSpecies()
        setSiteProposalFieldValidation({
          ...siteProposalFieldValidation,
          species: defaultSiteProposalValidationResults.species,
          updated: false,
        })
      },
    )
  }

  async function handleSensitiveWarningConfirm() {
    if (isSubmittingSensitiveWarning) {
      return
    }
    setIsSubmittingSensitiveWarning(true)

    try {
      const ackResponse =
        typeof requestSubmitSensitiveSpeciesAcknowledgment === "function"
          ? await requestSubmitSensitiveSpeciesAcknowledgment(siteId, historyId, taxonId)
          : null

      const alreadySubmitted =
        ackResponse?.alreadySubmitted === true ||
        ackResponse?.already_submitted === true ||
        (typeof ackResponse?.message === "string" && ackResponse.message.toLowerCase().includes("already"))

      if (getMySite) {
        await getMySite()
      }
      if (getSiteValidation) {
        await getSiteValidation()
      }

      const sensitiveEventDetail = { siteId, siteHistoryId: historyId, ...(taxonId != null && taxonId !== '' ? { taxonId } : {}) }
      globalThis.dispatchEvent(new CustomEvent("sensitiveSpeciesAcknowledged", { detail: sensitiveEventDetail }))
      globalThis.dispatchEvent(new CustomEvent("sensitiveSpeciesDataChanged", { detail: sensitiveEventDetail }))

      if (alreadySubmitted) {
        showSuccessToast(
          <FormattedMessage
            id="sensitiveSpeciesAlreadySubmitted"
            defaultMessage="This has already been submitted."
          />,
        )
      } else {
        showSuccessToast(
          <FormattedMessage
            id="sensitiveSpeciesSubmitted"
            defaultMessage="Your request to reassess this site has been received and will be processed shortly. Thank you for your patience."
          />,
        )
      }

      setIsSensitiveWarningOpen(false)
    } catch (error) {
      const message = typeof error?.message === "string" ? error.message.toLowerCase() : ""
      const alreadySubmitted = message.includes("already")
      showErrorToast(
        alreadySubmitted ? (
          <FormattedMessage
            id="sensitiveSpeciesAlreadySubmitted"
            defaultMessage="This has already been submitted."
          />
        ) : (
          <FormattedMessage
            id="sensitiveSpeciesSubmitError"
            defaultMessage="Failed to submit sensitive species data. Please try again."
          />
        ),
      )
    } finally {
      setIsSubmittingSensitiveWarning(false)
    }
  }

  function handleInputChange(e) {
    setScientificName(e)
    if (e.length > 4) {
      updateSearchOptionsTimeOut(() => getSpecies(e))
    } else {
      setSearchOptions([])
    }
  }

  function handleOpenEditModal(e) {
    // Species-level sensitive warning has been removed; always open details directly.
    setSpeciesToWorkWith(e)
    setIsSpeciesDetailsModalOpen(true)
  }

  function removeSpeciesFromList(id, historyIdParam) {
    requestDeleteSiteSpecie(id, params.siteHistoryId, historyIdParam).then(() => {
      getSiteSpecies()
      getNewSiteSpecies()
      getOldSiteSpecies()
    })
  }

  function openRemovalModal(specie) {
    setSpeciesToWorkWith(specie)
    setIsModalOpen(true)
  }

  function closeEditSpeciesModal() {
    const taxonTreatmentsToShowWarning = ["A-NR", "Q-NR", "S-NR", "U-NR", "A-UR"]
    const hasTaxonTreatment = speciesToWorkWith?.taxonTreatment
    if (!hasTaxonTreatment || !taxonTreatmentsToShowWarning.includes(speciesToWorkWith.taxonTreatment)) {
      setSpeciesToWorkWith(false)
      setIsSpeciesDetailsModalOpen(false)
      getSiteSpecies()
      getNewSiteSpecies()
      getOldSiteSpecies()
    }
  }

  // fetch from server (assessment or reassessment)
  const loadSpeciesFiles = useCallback(async () => {
    if (!siteId) return
    try {
      setIsFilesLoading(true)
      let res
      if (isReassessment) {
        try {
          res = await requestGetSpeciesFiles({ id: siteId, siteHistoryId: historyId })
        } catch {
          res = await requestGetSpeciesFiles(siteId, historyId)
        }
      } else {
        try {
          res = await requestGetSpeciesFiles({ id: siteId })
        } catch {
          res = await requestGetSpeciesFiles(siteId)
        }
      }

      let serverFiles = extractFiles(res).map(normFile)

      // For Secretariat/RFP on reassessment, fallback to base assessment files
      // when current reassessment does not have uploaded species documents.
      if (isSecretariatOrRfp && isReassessment && serverFiles.length === 0) {
        let fallbackRes
        try {
          fallbackRes = await requestGetSpeciesFiles({ id: siteId })
        } catch {
          fallbackRes = await requestGetSpeciesFiles(siteId)
        }
        serverFiles = extractFiles(fallbackRes).map(normFile)
      }

      // merge with cache
      let cached = []
      const merged = uniqByKey([...(cached || []), ...serverFiles])

      setUploadedFiles(merged)
      // Don't update site context with species files - they should be separate

    } catch {
      showErrorToast(<FormattedMessage id="loadFilesFailed" defaultMessage="Failed to load files" />)
    } finally {
      setIsFilesLoading(false)
    }
  }, [siteId, historyId, isReassessment, isSecretariatOrRfp])

  const renderSpeciesListToConfirmApprovedBySecretariat = speciesList
    .filter((item) => item.isApprovedBySecretariat)
    .map((item, index) => {
      const qualified = isSpeciesQualifiedForReassessment(item)
      const isDisqualifiedIba = isIbaSpeciesDisqualified(item)
      const speciesKey = `${item.historyId ?? item.id ?? item.taxonid ?? item.scientificName ?? 'species'}-approved`
      return (
        <SpeciesConfirmDeclineList
          getSiteSpecies={getSiteSpecies}
          site={site}
          item={item}
          lastItem={index + 1 === speciesList.length}
          key={speciesKey}
          permitDelete={site.SiteStatusId === SITE_STATUS_DRAFT_ID}
          nonQualified={!qualified || isDisqualifiedIba}
          onClick={handleOpenEditModal}
          openRemovalModal={openRemovalModal}
        />
      )
    })

  const renderSpeciesListToConfirmRejectedBySecretariat = rejectedSpecies
    .filter((item) => !item.isApprovedBySecretariat)
    .map((item, index) => {
      const qualified = isSpeciesQualifiedForReassessment(item)
      const isDisqualifiedIba = isIbaSpeciesDisqualified(item)
      const speciesKey = `${item.historyId ?? item.id ?? item.taxonid ?? item.scientificName ?? 'species'}-rejected`
      return (
        <SpeciesConfirmDeclineList
          getSiteSpecies={getSiteSpecies}
          site={site}
          item={item}
          lastItem={index + 1 === speciesList.length}
          key={speciesKey}
          nonQualified={!qualified || isDisqualifiedIba}
          onClick={handleOpenEditModal}
          openRemovalModal={openRemovalModal}
        />
      )
    })

  const renderSpeciesListNewAssessment = newSpecies.map((item, index) => (
    <SpeciesConfirmDeclineList
      getSiteSpecies={getNewSiteSpecies}
      site={site}
      item={item}
      lastItem={index + 1 === newSpecies.length}
      key={`${item.historyId ?? item.id ?? item.taxonid ?? item.scientificName ?? 'species'}-new`}
      onClick={handleOpenEditModal}
      permitDelete={true}
      openRemovalModal={openRemovalModal}
    />
  ))

  const renderSpeciesPreviousAssessment = oldSpecies
    .filter((item) => item.lastReassessmentDate)
    .map((item, index) => {
      const qualified = isSpeciesQualifiedForReassessment(item)
      const isDisqualifiedIba = isIbaSpeciesDisqualified(item)
      const speciesKey = `${item.historyId ?? item.id ?? item.taxonid ?? item.scientificName ?? 'species'}-old`
      return (
        <SpeciesConfirmDeclineList
          getSiteSpecies={getOldSiteSpecies}
          site={site}
          item={item}
          lastItem={index + 1 === oldSpecies.length}
          key={speciesKey}
          nonQualified={!qualified || isDisqualifiedIba}
          onClick={handleOpenEditModal}
          openRemovalModal={openRemovalModal}
        />
      )
    })

  useEffect(() => {
    getNewSiteSpecies()
  }, [getNewSiteSpecies])

  useEffect(() => {
    getOldSiteSpecies()
  }, [getOldSiteSpecies])

  useEffect(() => {
    getSiteSpecies()
  }, [getSiteSpecies])

  useEffect(() => {
    if (siteId) {
      loadSpeciesFiles()
    }
  }, [siteId, loadSpeciesFiles])

  // Build uploader params
  const uploaderParams = useMemo(() => {
    const base = { id: site.id }
    if (historyId) base.siteHistoryId = historyId
    return base
  }, [site.id, historyId])

  // Make sure Uploader gets SAS-first URLs
  const uploaderFiles = useMemo(() => (uploadedFiles || []).map((f) => normFile(f)), [uploadedFiles])

  return (
    <SpeciesDetailsSearchFormWrapper>
      <Container>
        <Row>
          <Col md={8} mdOffset={2}>
            <AssessmentPageTittleWrapper firstTitle={true}>
              <AssessmentPageTittle data-test="legend">
                <FormattedMessage id="speciesDetails" />
              </AssessmentPageTittle>
              <AssessmentPageSubTittleWrapper marginBottom="48px">
                <AssessmentPageSubTittle>
                  <FormattedMessage id="speciesDetailsSubTittle" />
                </AssessmentPageSubTittle>
                <Tooltip
                  style={{ zIndex: 1000 }}
                  placement="bottom"
                  trigger={["hover"]}
                  align={{ offset: [tooltipXOffset, tooltipYOffset] }}
                  overlay={{ label: "speciesDetailSubTittle", infoTooltip: "speciesDetailSubTittleHint" }}
                  link={"info@keybiodiversityareas.org"}
                >
                  <AssessmentPageSubTittleIconWrapper>
                    <InfoIcon />
                  </AssessmentPageSubTittleIconWrapper>
                </Tooltip>
              </AssessmentPageSubTittleWrapper>
            </AssessmentPageTittleWrapper>
          </Col>
        </Row>
        {site.SiteStatusId === SITE_STATUS_NOMINATED_ID ? (
          <Row>
            {renderSpeciesListToConfirmApprovedBySecretariat.length > 0 && (
              <Col md={8} mdOffset={2}>
                <Row>
                  <Col md={8} mdOffset={2}>
                    <SpeciesListTitleWrapper>
                      <SpeciesListTitle marginTop="0">
                        <FormattedMessage id="approvedTriggers" />
                      </SpeciesListTitle>
                    </SpeciesListTitleWrapper>
                  </Col>
                </Row>
                <LoadingWrapper size={"24px"} isLoading={isFilesLoading || isLoading}>
                  {renderSpeciesListToConfirmApprovedBySecretariat}
                  <SpeciesPaginationWrapper>
                    <ItemsPerPageDropdown
                      currentPage={allSpeciesPagination.pagination.currentPage}
                      resultsPerPage={allSpeciesPagination.pagination.resultsPerPage}
                      totalNumber={allSpeciesPagination.pagination.totalNumber}
                      onChange={allSpeciesPagination.changeResultsPerPage}
                      dataTest="sites-items-per-page"
                    />
                    {allSpeciesPagination.pagination.totalPages > 1 && (
                      <Pagination
                        activePages={allSpeciesPagination.pagination.currentPage}
                        numberOfPages={allSpeciesPagination.pagination.totalPages}
                        nextPage={allSpeciesPagination.nextPage}
                        prevPage={allSpeciesPagination.prevPage}
                        nextSection={allSpeciesPagination.nextSection}
                        prevSection={allSpeciesPagination.prevSection}
                        selectPage={allSpeciesPagination.selectPage}
                        dataTest="page-number"
                      />
                    )}
                  </SpeciesPaginationWrapper>
                </LoadingWrapper>
              </Col>
            )}
            {renderSpeciesListToConfirmRejectedBySecretariat.length > 0 && (
              <Col md={8} mdOffset={2}>
                <Row>
                  <Col md={8} mdOffset={2}>
                    <SpeciesListTitleWrapper>
                      <SpeciesListTitle marginTop="48px">
                        <FormattedMessage id="rejectedTriggers" />
                      </SpeciesListTitle>
                    </SpeciesListTitleWrapper>
                  </Col>
                </Row>
                <LoadingWrapper size={"24px"} isLoading={isFilesLoading || isLoading}>
                  {renderSpeciesListToConfirmRejectedBySecretariat}
                </LoadingWrapper>
              </Col>
            )}
          </Row>
        ) : (
          <>
            <Row>
              <Col md={8} mdOffset={2}>
                <SpeciesSearchContainer disabled={disabled}>
                  <SearchDropdown
                    value={species}
                    onChange={setSpecies}
                    options={searchOptions}
                    placeholder="searchByScientificName"
                    field="speciesDetails"
                    buttonName="addSpecies"
                    buttonDisabled={!species}
                    height="40px"
                    marginBottom="0"
                    marginTop="0"
                    menuWidth="416px"
                    buttonOnClick={addSpeciesToSite}
                    inputValue={scientificName}
                    onInputChange={handleInputChange}
                    disabled={disabled}
                  />
                </SpeciesSearchContainer>
              </Col>
            </Row>
            {renderSpeciesListNewAssessment.length > 0 && (
              <>
                <Row>
                  <Col md={8} mdOffset={2}>
                    <SpeciesListTitleWrapper>
                      <SpeciesListTitle>
                        <FormattedMessage id="newTriggers" />
                      </SpeciesListTitle>
                    </SpeciesListTitleWrapper>
                  </Col>
                </Row>
                <Row>
                  <Col md={8} mdOffset={2} dataTest="new-added-species">
                    <LoadingWrapper size={"24px"} isLoading={isFilesLoading || isLoading}>
                      {renderSpeciesListNewAssessment}
                      <SpeciesPaginationWrapper>
                        <ItemsPerPageDropdown
                          currentPage={newSpeciesPagination.pagination.currentPage}
                          resultsPerPage={newSpeciesPagination.pagination.resultsPerPage}
                          totalNumber={newSpeciesPagination.pagination.totalNumber}
                          onChange={newSpeciesPagination.changeResultsPerPage}
                          dataTest="sites-items-per-page"
                        />
                        {newSpeciesPagination.pagination.totalPages > 1 && (
                          <Pagination
                            activePages={newSpeciesPagination.pagination.currentPage}
                            numberOfPages={newSpeciesPagination.pagination.totalPages}
                            nextPage={newSpeciesPagination.nextPage}
                            prevPage={newSpeciesPagination.prevPage}
                            nextSection={newSpeciesPagination.nextSection}
                            prevSection={newSpeciesPagination.prevSection}
                            selectPage={newSpeciesPagination.selectPage}
                            dataTest="page-number"
                          />
                        )}
                      </SpeciesPaginationWrapper>
                    </LoadingWrapper>
                  </Col>
                </Row>
              </>
            )}
            {renderSpeciesPreviousAssessment.length > 0 && (
              <>
                <Row>
                  <Col md={8} mdOffset={2}>
                    <SpeciesListTitleWrapper>
                      <SpeciesListTitle marginTop={renderSpeciesListNewAssessment.length > 0 ? "48px" : "0"}>
                        <FormattedMessage id="existingTriggers" />
                      </SpeciesListTitle>
                    </SpeciesListTitleWrapper>
                  </Col>
                </Row>
                <Row>
                  <Col md={8} mdOffset={2} dataTest="existing-species">
                    <LoadingWrapper size={"24px"} isLoading={isFilesLoading || isLoading}>
                      {renderSpeciesPreviousAssessment}
                      <SpeciesPaginationWrapper>
                        <ItemsPerPageDropdown
                          currentPage={oldSpeciesPagination.pagination.currentPage}
                          resultsPerPage={oldSpeciesPagination.pagination.resultsPerPage}
                          totalNumber={oldSpeciesPagination.pagination.totalNumber}
                          onChange={oldSpeciesPagination.changeResultsPerPage}
                          dataTest="sites-items-per-page"
                        />
                        {oldSpeciesPagination.pagination.totalPages > 1 && (
                          <Pagination
                            activePages={oldSpeciesPagination.pagination.currentPage}
                            numberOfPages={oldSpeciesPagination.pagination.totalPages}
                            nextPage={oldSpeciesPagination.nextPage}
                            prevPage={oldSpeciesPagination.prevPage}
                            nextSection={oldSpeciesPagination.nextSection}
                            prevSection={oldSpeciesPagination.prevSection}
                            selectPage={oldSpeciesPagination.selectPage}
                            dataTest="page-number"
                          />
                        )}
                      </SpeciesPaginationWrapper>
                    </LoadingWrapper>
                  </Col>
                </Row>
              </>
            )}
          </>
        )}
        {showUploader && (
          <Row>
            <Col md={8} mdOffset={2} style={{ marginTop: "32px" }}>
              <SpeciesListTitleWrapper>
                <SpeciesListTitle></SpeciesListTitle>
              </SpeciesListTitleWrapper>

              <UploadSiteFiles
                label="uploadSiteFiles"
                field="uploadSiteFiles"
                infoTooltip="speciesUploaderHint"
                height="128px"
                dataTest="uploadSiteFiles-text-area"
                isOptional={true}
                disabled={disabled}
              />

              <Uploader
                key={`${siteId}-${historyId ?? "assessment"}-${uploadedFiles?.length}-${uploadedFiles
                  ?.map((f) => f.id || f.blob_name || f.file_name || f.name)
                  .join("|")}`}
                params={uploaderParams}
                allowDownloadsWhenConfirmed={isKbaSecOrRfp}
                hideDownload={hideDownload}
                siteFiles={uploaderFiles}
                disabled={disabled}
                fileType="species"
                fetchFilesFn={async (siteId, siteHistoryId) => {
                  try {
                    let res
                    if (isReassessment) {
                      try {
                        res = await requestGetSpeciesFiles({ id: siteId, siteHistoryId: siteHistoryId })
                      } catch {
                        res = await requestGetSpeciesFiles(siteId, siteHistoryId)
                      }
                    } else {
                      try {
                        res = await requestGetSpeciesFiles({ id: siteId })
                      } catch {
                        res = await requestGetSpeciesFiles(siteId)
                      }
                    }
                    const primaryFiles = extractFiles(res).map(normFile)
                    if (isSecretariatOrRfp && isReassessment && primaryFiles.length === 0) {
                      try {
                        return await requestGetSpeciesFiles({ id: siteId })
                      } catch {
                        return await requestGetSpeciesFiles(siteId)
                      }
                    }

                    return res
                  } catch {
                    return { files: [] }
                  }
                }}
                uploadFn={async (siteId, siteHistoryId, formData, options) => {
                  try {
                    const response = await requestUpdateSpeciesFiles(siteId, siteHistoryId, formData, options)
                    
                    let nextFiles = extractFiles(response).map(normFile)
                    if (!nextFiles.length) {
                      let res
                      try {
                        res = isReassessment
                          ? await requestGetSpeciesFiles({ id: siteId, siteHistoryId: historyId })
                          : await requestGetSpeciesFiles({ id: siteId })
                      } catch {
                        res = isReassessment
                          ? await requestGetSpeciesFiles(siteId, historyId)
                          : await requestGetSpeciesFiles(siteId)
                      }
                      nextFiles = extractFiles(res).map(normFile)
                    }
                    const merged = uniqByKey(nextFiles)
                    setUploadedFiles(merged)
                    // Don't update site context with species files - they should be separate
                    return response
                  } catch (error) {
                    showErrorToast(<FormattedMessage id="uploadFailed" />)
                    throw error
                  }
                }}
                deleteFn={async (...args) => {
                  try {
                    const response = await requestDeleteSpeciesFiles(...args)
                    let nextFiles = extractFiles(response).map(normFile)
                    if (!nextFiles.length) {
                      let res
                      try {
                        res = isReassessment
                          ? await requestGetSpeciesFiles({ id: siteId, siteHistoryId: historyId })
                          : await requestGetSpeciesFiles({ id: siteId })
                      } catch {
                        res = isReassessment
                          ? await requestGetSpeciesFiles(siteId, historyId)
                          : await requestGetSpeciesFiles(siteId)
                      }
                      nextFiles = extractFiles(res).map(normFile)
                    }
                    const merged = uniqByKey(nextFiles)
                    setUploadedFiles(merged)
                    // Don't update site context with species files - they should be separate
                    return response
                  } catch (error) {
                    showErrorToast(<FormattedMessage id="deleteFailed" />)
                    throw error
                  }
                }}
              />

              <div style={{ marginTop: "8px", fontSize: "14px" }} />
            </Col>
          </Row>
        )}
      </Container>

      <SensitiveSpeciesWarningModal
        isOpen={isSensitiveWarningOpen}
        onConfirm={handleSensitiveWarningConfirm}
        fullAccess={false}
        onClose={() => {
          if (!isSubmittingSensitiveWarning) {
            setIsSensitiveWarningOpen(false)
          }
        }}
        isLoading={isSubmittingSensitiveWarning}
      />

      <Modal
        speciesToReassess={speciesToWorkWith}
        isOpen={isSpeciesDetailsModalOpen}
        toggleModal={closeEditSpeciesModal}
        width="1088px"
        modalTitleId="speciesAssessment"
        padding="0px"
        bigModal={true}
        autoSaveIcon={true}
        isDraft={normalizedStatusId === SITE_STATUS_DRAFT_ID}
        cancelHandler={
          speciesToWorkWith.lastReassessmentDate && isNcgOrIndividualUser && normalizedStatusId === SITE_STATUS_DRAFT_ID
            ? () => setIsOpen(true)
            : false
        }
      >
        <SpeciesDetailsModal
          closeEditSpeciesModal={closeEditSpeciesModal}
          openCancelModal={() => setIsOpen(true)}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          item={speciesToWorkWith}
        />
      </Modal>

      <Modal
        width={"416px"}
        isOpen={isModalOpen}
        toggleModal={setIsModalOpen}
        modalTitleId="removeSpecies"
        bottomBorder={true}
      >
        <RemoveSpeciesModal
          itemName={speciesToWorkWith.scientificName}
          item={speciesToWorkWith}
          toggleModal={setIsModalOpen}
          confirm={removeSpeciesFromList}
        />
      </Modal>
    </SpeciesDetailsSearchFormWrapper>
  )
}

export default SpeciesDetailsSearchForm

SpeciesDetailsSearchForm.propTypes = {
  site: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    siteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    SiteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    SiteStatusId: PropTypes.number.isRequired,
    siteHistoryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    sitehistoryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    SiteHistoryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    historyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    SiteHistory: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    }),
    siteHistory: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    }),
    hasSensitiveSpecies: PropTypes.bool,
    has_sensitive_species: PropTypes.bool,
    hasSensitive: PropTypes.bool,
    sensitiveSpeciesCount: PropTypes.number,
    sensitive_species_count: PropTypes.number
  }).isRequired
}
